import { Flex } from 'components/shared/flex'
import { usePostProductSetApi, usePutProductSetApi } from 'api/product_set'
import { CheckboxField, InputField, MultipleSelectField, SelectField } from 'components/shared/input'
import { GlobalStateContext } from 'contexts/global_state_context'
import { useContext, useEffect, useState } from 'react'
import { Button, Modal } from 'react-bootstrap'
import { Form, useEffectSkipFirst, useForm } from 'utils/hooks'
import { ApiError } from 'utils/network/api_hooks'
import { ProductSet, ProductSetForm } from 'entities/product_set'
import { useFetchProductItemsApi } from 'api/product_item'

export type FormViewProps = {
    form: Form<ProductSetForm>
    apiError: ApiError
}

const FormView = ({ form, apiError }: FormViewProps) => {
    const productItemsApi = useFetchProductItemsApi()

    useEffect(() => {
        productItemsApi.execute()
    }, [])

    return (
        <>
            <InputField form={form} attr={'displayName'} apiError={apiError} label="表示名" required />
            <InputField form={form} attr={'name'} apiError={apiError} label="CRチーム管理名" required />
            <Flex>
                <InputField form={form} attr={'price'} apiError={apiError} label="価格(税抜)" type="number" required />
                <SelectField
                    form={form}
                    attr={'gender'}
                    apiError={apiError}
                    label="対象の性別"
                    selectItems={[
                        { label: '男性', value: 'MALE' },
                        { label: '女性', value: 'FEMALE' },
                    ]}
                    blankLabel="指定なし"
                    includeBlank
                />
            </Flex>
            <InputField form={form} attr={'note'} apiError={apiError} label="メモ" placeholder="〇〇病院用など" />
            <CheckboxField form={form} attr="isForeignCountry" label="海外キット対応" checkboxLabel="海外キット対応" style={{ paddingTop: '8px' }} />
            <MultipleSelectField
                id="product-set-product-items"
                form={form}
                attr={'productItemIds'}
                label="製品"
                selectItems={productItemsApi.response.results.map((pi) => {
                    return { label: pi.name, value: pi.id! }
                })}
                hideSearch
                horizontal
            />
        </>
    )
}

type NewProductSetFormProps = {
    onComplete?: () => void
}

export const NewProductSetForm = (props: NewProductSetFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = usePostProductSetApi()
    const form = useForm<ProductSetForm>({})
    const [show, setShow] = useState<boolean>(false)

    const handleUpdate = () => {
        api.execute(form)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                setShow(false)
                props.onComplete()
            }
        }
    }, [api.loading])

    const handleClose = () => {
        setShow(false)
    }

    return (
        <>
            <Button
                variant="success"
                onClick={() => {
                    setShow(true)
                }}
            >
                新規登録
            </Button>
            <Modal show={show} onHide={handleClose}>
                <Modal.Header closeButton>
                    <Modal.Title>プロダクトセットの登録</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <>
                        <FormView form={form} apiError={api.apiError} />
                    </>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        閉じる
                    </Button>
                    <Button variant="success" onClick={handleUpdate}>
                        登録
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}
type EditProductSetFormProps = {
    productSet: ProductSet
    show: boolean
    onClose: () => void
    onComplete?: () => void
}

export const EditProductSetForm = (props: EditProductSetFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = usePutProductSetApi()
    const form = useForm<ProductSetForm>(props.productSet)

    useEffectSkipFirst(() => {
        form.set(props.productSet)
    }, [props.productSet])

    const handleUpdate = () => {
        api.execute(form)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                props.onComplete()
            }
        }
    }, [api.loading])

    return (
        <Modal show={props.show} onHide={props.onClose}>
            <Modal.Header closeButton>
                <Modal.Title>プロダクトセットの編集</Modal.Title>
            </Modal.Header>
            <Modal.Body>{form.object.id && <FormView form={form} apiError={api.apiError} />}</Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={props.onClose}>
                    閉じる
                </Button>
                <Button variant="success" onClick={handleUpdate}>
                    更新
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
